/* eslint-disable class-methods-use-this */
import * as React from 'react';
import { Box } from '@chakra-ui/react';
import {
  Cell,
  keyCodes,
  Uncertain,
  Compatible,
  CellTemplate,
  getCellProperty,
  UncertainCompatible,
} from '@silevis/reactgrid';

export interface ModernCheckboxCell extends Cell {
  type: 'checkbox';
  extraKey?: string;
  checked: boolean;
  checkedText?: string;
  uncheckedText?: string;
}

export class ModernCheckboxCellTemplate implements CellTemplate<ModernCheckboxCell> {
  getCompatibleCell(uncertainCell: Uncertain<ModernCheckboxCell>): Compatible<ModernCheckboxCell> {
    const checked = getCellProperty(uncertainCell, 'checked', 'boolean');
    const text = checked ? uncertainCell.checkedText ?? '1' : uncertainCell.uncheckedText ?? '';
    return { ...uncertainCell, checked: !!checked, value: checked ? 1 : Number.NaN, text };
  }

  handleKeyDown(
    cell: Compatible<ModernCheckboxCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
  ): { cell: Compatible<ModernCheckboxCell>; enableEditMode: boolean } {
    if (!shift && (keyCode === keyCodes.SPACE || keyCode === keyCodes.ENTER))
      return { cell: this.getCompatibleCell(this.toggleCheckboxCell(cell)), enableEditMode: false };
    return { cell, enableEditMode: false };
  }

  private toggleCheckboxCell(cell: Compatible<ModernCheckboxCell>): Compatible<ModernCheckboxCell> {
    return this.getCompatibleCell({ ...cell, checked: !cell.checked });
  }

  update(
    cell: Compatible<ModernCheckboxCell>,
    cellToMerge: UncertainCompatible<ModernCheckboxCell>,
  ): Compatible<ModernCheckboxCell> {
    const checked = cellToMerge.type === 'checkbox' ? cellToMerge.checked : !!cellToMerge.value;
    return this.getCompatibleCell({ ...cell, checked });
  }

  getClassName(cell: Compatible<ModernCheckboxCell>): string {
    return cell.className ?? '';
  }

  render(
    cell: Compatible<ModernCheckboxCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<ModernCheckboxCell>, commit: boolean) => void,
  ): React.ReactNode {
    // Plain div, not Chakra's `<Checkbox>` — inside a ReactGrid cell, Chakra's
    // hidden-native-input + sibling-selector styling kept losing to
    // ReactGrid's own default `.rg-checkbox-cell input` CSS (its stylesheet
    // is built for a plain `<label><input/><span/></label>`, not Chakra's
    // DOM), so every attempt at custom `sx` — and even the native
    // `colorScheme='purple'` prop — rendered as an unfilled, bordered box
    // instead of solid purple. Building it from scratch sidesteps that
    // conflict entirely: same look as `TableCheckBox`'s filled state, just
    // with no Chakra/ReactGrid CSS to fight.
    return (
      <Box
        as='button'
        type='button'
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          onCellChanged(this.toggleCheckboxCell(cell), true);
        }}
        onPointerDown={(event: React.PointerEvent) => event.stopPropagation()}
        display='flex'
        alignItems='center'
        justifyContent='center'
        width='16px'
        height='16px'
        borderRadius='4px'
        border='1px solid'
        borderColor={cell.checked ? '#8B5CF6' : '#D1D6DD'}
        bg={cell.checked ? '#8B5CF6' : 'white'}
        cursor='pointer'
        flexShrink={0}
      >
        {cell.checked && (
          <svg width='10' height='8' viewBox='0 0 10 8' fill='none'>
            <path
              d='M1 4L3.5 6.5L9 1'
              stroke='white'
              strokeWidth='1.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
      </Box>
    );
  }
}
